import { createSlice } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { WarehouseDetailsType, WarehouseListItemType, WarehouseType } from '../../types/warehouse/warehouse.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  deleteMicroserviceWarehouseDetails,
  getMicroserviceWarehouseDetails,
  getMicroserviceWarehousesList,
  patchMicroserviceWarehouseDetails,
  postMicroserviceWarehouseDetails,
} from './warehouses.action';
import snackbarUtils from '../../utils/SnackbarUtils';

export interface WarehousesState {
  microserviceWarehousesList: null | PaginationType<WarehouseListItemType>;
  microserviceWarehousesListFetchStatus: FETCH_STATUS;
  microserviceWarehousesRouteParams: RouteParamsType;
  microserviceWarehouseDetails: null | WarehouseDetailsType;
  microserviceWarehouseDetailsFetchStatus: FETCH_STATUS;
  microserviceWarehouseDetailsPostStatus: FETCH_STATUS;
  microserviceWarehouseDetailsPost:
    | null
    | (Pick<WarehouseType, 'id' | 'created_at' | 'modified_at' | 'name'> & {
        address: { id: number };
        organization: { id: number };
      });
  microserviceWarehouseDetailsPatchStatus: FETCH_STATUS;
  microserviceWarehouseDetailsDeleteStatus: FETCH_STATUS;
}

export const initialWarehousesRouteParams = {
  page: 1,
  limit: 10,
  order: '-warehouse.id',
  q: '',
  country_id: '',
  country_category: '',
};

const INIT_STATE: WarehousesState = {
  microserviceWarehousesRouteParams: initialWarehousesRouteParams,
  microserviceWarehousesList: null,
  microserviceWarehousesListFetchStatus: FETCH_STATUS.NULL,
  microserviceWarehouseDetails: null,
  microserviceWarehouseDetailsFetchStatus: FETCH_STATUS.NULL,
  microserviceWarehouseDetailsPostStatus: FETCH_STATUS.NULL,
  microserviceWarehouseDetailsPost: null,
  microserviceWarehouseDetailsPatchStatus: FETCH_STATUS.NULL,
  microserviceWarehouseDetailsDeleteStatus: FETCH_STATUS.NULL,
};

export const warehousesSlice = createSlice({
  name: 'warehouses',
  initialState: INIT_STATE,
  reducers: {
    setMicroserviceWarehousesList: (state, action) => {
      state.microserviceWarehousesList = action.payload;
    },
    setMicroserviceWarehousesListFetchStatus: (state, action) => {
      state.microserviceWarehousesListFetchStatus = action.payload;
    },
    setMicroserviceWarehouseDetails: (state, action) => {
      state.microserviceWarehouseDetails = action.payload;
    },
    setMicroserviceWarehouseDetailsFetchStatus: (state, action) => {
      state.microserviceWarehouseDetailsFetchStatus = action.payload;
    },
    setMicroserviceWarehouseDetailsPostStatus: (state, action) => {
      state.microserviceWarehouseDetailsPostStatus = action.payload;
    },
    setMicroserviceWarehouseDetailsPost: (state, action) => {
      state.microserviceWarehouseDetailsPost = action.payload;
    },
    setMicroserviceWarehouseDetailsPatchStatus: (state, action) => {
      state.microserviceWarehouseDetailsPatchStatus = action.payload;
    },
    setMicroserviceWarehouseDetailsDeleteStatus: (state, action) => {
      state.microserviceWarehouseDetailsDeleteStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;

      if (payload.reducer === 'microservice-warehouses') {
        state.microserviceWarehousesRouteParams = { ...state.microserviceWarehousesRouteParams, ...payload.data };
      }
    });

    // CREATE WAREHOUSES DETAILS
    builder.addCase(postMicroserviceWarehouseDetails.pending, state => {
      state.microserviceWarehouseDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMicroserviceWarehouseDetails.rejected, (state, action) => {
      state.microserviceWarehouseDetailsPostStatus = FETCH_STATUS.REJECTED;
      state.microserviceWarehouseDetailsPost = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMicroserviceWarehouseDetails.fulfilled, (state, action) => {
      state.microserviceWarehouseDetailsPostStatus = FETCH_STATUS.FULFILLED;
      state.microserviceWarehouseDetailsPost = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Warehouse created successfully');
      });
    });

    // GET WAREHOUSES LIST
    builder.addCase(getMicroserviceWarehousesList.pending, state => {
      state.microserviceWarehousesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMicroserviceWarehousesList.rejected, (state, action) => {
      if (action.payload) {
        state.microserviceWarehousesListFetchStatus = FETCH_STATUS.REJECTED;
        // state.microserviceWarehousesList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMicroserviceWarehousesList.fulfilled, (state, action) => {
      state.microserviceWarehousesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.microserviceWarehousesList = action.payload;
    });

    // GET WAREHOUSES DETAILS
    builder.addCase(getMicroserviceWarehouseDetails.pending, state => {
      state.microserviceWarehouseDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMicroserviceWarehouseDetails.rejected, (state, action) => {
      state.microserviceWarehouseDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.microserviceWarehouseDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getMicroserviceWarehouseDetails.fulfilled, (state, action) => {
      state.microserviceWarehouseDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.microserviceWarehouseDetails = action.payload;
    });

    // PATCH WAREHOUSES DETAILS
    builder.addCase(patchMicroserviceWarehouseDetails.pending, state => {
      state.microserviceWarehouseDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchMicroserviceWarehouseDetails.rejected, (state, action) => {
      state.microserviceWarehouseDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchMicroserviceWarehouseDetails.fulfilled, state => {
      state.microserviceWarehouseDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Warehouse updated successfully');
      });
    });

    // DELETE WAREHOUSES DETAILS
    builder.addCase(deleteMicroserviceWarehouseDetails.pending, state => {
      state.microserviceWarehouseDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMicroserviceWarehouseDetails.rejected, (state, action) => {
      state.microserviceWarehouseDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMicroserviceWarehouseDetails.fulfilled, state => {
      state.microserviceWarehouseDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Warehouse deleted successfully');
      });
    });
  },
});

export const {
  setMicroserviceWarehousesList,
  setMicroserviceWarehouseDetailsDeleteStatus,
  setMicroserviceWarehouseDetailsFetchStatus,
  setMicroserviceWarehouseDetailsPatchStatus,
  setMicroserviceWarehouseDetailsPostStatus,
  setMicroserviceWarehousesListFetchStatus,
  setMicroserviceWarehouseDetails,
  setMicroserviceWarehouseDetailsPost,
} = warehousesSlice.actions;

export default warehousesSlice.reducer;
